import React, { useEffect, useMemo, useState } from 'react';
import { BaseModal, Button, ToastMessage } from '@cokitchen/cokitchen-components';
import { AddCircleIcon } from 'cokitchen-icons';
import { AddonSelectionType, MultiselectOptionsType } from 'utils/types';
import { apiService } from 'services/base.service';
import { toast } from 'react-toastify';
import Select from 'components/base/select';

interface Props {
  isOpen: boolean;
  brands: MultiselectOptionsType[];
  defaultBrands: string[];
  meals: MultiselectOptionsType[];
  defaultParentMeals: Record<string, string>[];
  onClose: (items?: any) => void;
}

export interface NewAddonCategoryDataType {
  id: string;
  name: string;
  brandName: string;
  summary: string;
  selectionType: AddonSelectionType;
  minSelection: number;
  maxSelection: number;
}

export default function NewAddonCategoryModal(props: Props) {
  const { isOpen, brands, defaultBrands, meals, defaultParentMeals, onClose } = props;
  const [selectedBrands, setSelectedBrands] = useState<string[]>(defaultBrands);
  const [selectedParentMeals, setSelectedParentMeals] = useState<string[]>(
    defaultParentMeals.map((meal) => meal.value)
  );
  const [selectionType] = useState('');
  const [payload, setPayload] = useState<Record<string, any>>({
    parent_meal: selectedParentMeals
  });
  const [loading, setLoading] = useState(false);
  const [parentMealsList, setParentMealsList] = useState(meals);

  async function fetchParentMeals() {
    try {
      const res = await apiService.post(`meals`, {
        data: { brand_ids: selectedBrands },
        isInternal: true
      });
      const meals = res.data.data;
      const mealsList = meals.map((parentMeal: any) => {
        return {
          value: parentMeal?.id,
          text: `${parentMeal?.brand?.name} - ${parentMeal?.name}`,
          category_id: parentMeal?.meal_category?.id
        };
      });
      const allMeals = parentMealsList.concat(mealsList);
      const uniqueMeals: any[] = [];
      for (const meal of allMeals) {
        if (!uniqueMeals.find((_meal) => _meal.value === meal.value)) {
          uniqueMeals.push(meal);
        }
      }
      setParentMealsList(uniqueMeals);
    } catch (e) {
      console.log('error', e);
    }
  }

  async function onSubmit() {
    setLoading(true);
    try {
      payload.meal_category_selection_type_id = selectionType;
      payload.parent_meal = parentMealsList
        .filter((option) => payload.parent_meal.includes(option.value))
        .map((option: any) => ({
          meal_parent_id: option.value,
          meal_category_id: option.category_id
        }));
      const res = await apiService.post('marketing/meal-addon-category', {
        data: payload
      });
      const response = res.data.data[0];
      const newAddonCategory = {
        value: response?.id,
        text: `${response?.name} - ${response?.meal.name}`
      };
      onClose(newAddonCategory);
    } catch (error: any) {
      toast(<ToastMessage type='error' message={error?.response?.data?.message} />);
    }
    setLoading(false);
  }

  useEffect(() => {
    fetchParentMeals();
  }, [selectedBrands]);

  const isValid = useMemo(() => {
    return (
      selectedBrands?.length &&
      ['parent_meal', 'name', 'summary', 'min_selection_no', 'selection_no'].every(
        (key) => Object.keys(payload).includes(key) && Boolean(payload[key])
      )
    );
  }, [payload, selectionType]);

  return (
    <BaseModal
      isOpen={isOpen}
      title=''
      onRequestClose={() => {
        onClose();
        setPayload({});
      }}
      hasBackButton={false}
      titleIcon={<AddCircleIcon className='text-gray-5' />}
      titleIconColor='gray'>
      <div className=' flex flex-col space-y-4'>
        <h4 className='text-center text-xl font-semibold'>New Addon Category</h4>

        <div>
          <p className='mb-2 font-[500]'>Select brand(s)</p>
          <Select
            multiple
            hasSearch
            placeholder='Select brand(s) '
            values={selectedBrands}
            options={brands}
            onChange={(e) => setSelectedBrands(e)}
          />
        </div>

        <div className='mb-[24px]'>
          <p className='font-medium text-sm'>Select Parent meal(s)</p>
          <Select
            multiple
            hasSearch
            placeholder='Select parent meal(s) '
            values={selectedParentMeals}
            options={parentMealsList}
            onChange={(e) => {
              setSelectedParentMeals(e);
              setPayload((p) => ({ ...p, parent_meal: e }));
            }}
          />
        </div>

        <Button
          color='primary'
          variant='block'
          small
          loading={loading}
          onClick={() => onSubmit()}
          disabled={!isValid}>
          Confirm
        </Button>
      </div>
    </BaseModal>
  );
}
